'use client';

import { useEffect, useState } from 'react';
import { signOut } from 'next-auth/react';
import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '@/redux/Hook';
import { getActivationStatus } from '@/redux/subscriptionPlan/ThunkAction';
import SubscriptionPurchaseModal from '@/app/shared/subscription-plan/subscription-plan-purchase-form';

// Wholesalers (suppliers) keep read access through a 3-day grace period after their plan or trial ends.
// After that the panel is replaced by a subscribe screen until they renew.
export default function SubscriptionLockGate({
  isPublic,
  children,
}: {
  isPublic: boolean;
  children: React.ReactNode;
}) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const { activationStatus } = useAppSelector((state) => state.subscriptionPlan);
  const [showPurchase, setShowPurchase] = useState(false);
  const isWholesaler = user?.role === 'WHOLESALER';

  useEffect(() => {
    if (isWholesaler && !isPublic) dispatch(getActivationStatus());
  }, [dispatch, isWholesaler, isPublic]);

  if (isPublic || !isWholesaler || !activationStatus?.locked) return <>{children}</>;

  const endedOn = activationStatus.data?.endDate
    ? new Date(activationStatus.data.endDate).toLocaleDateString()
    : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-lg">
        <div className="mb-3 text-4xl">🔒</div>
        <h1 className="mb-2 text-xl font-semibold text-gray-900">Your plan has expired</h1>
        <p className="mb-6 text-sm text-gray-600">
          {endedOn ? `Your plan ended on ${endedOn} and the 3-day grace period is over. ` : 'The 3-day grace period is over. '}
          Subscribe to get back into your dashboard, products and orders.
        </p>
        <div className="flex flex-col gap-3">
          <Button onClick={() => setShowPurchase(true)}>Subscribe now</Button>
          <Button variant="outline" onClick={() => signOut({ callbackUrl: `${window.location.origin}/signin` })}>
            Sign out
          </Button>
        </div>
      </div>
      {showPurchase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-[90%] max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <SubscriptionPurchaseModal
              isOpen={showPurchase}
              onClose={() => setShowPurchase(false)}
              showCloseButton={showPurchase}
              isUpgradeFlow={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
